import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useQueryClient } from "react-query";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import { toast } from "../../../../../components/toastNotification";
import useAddTexasGrouping from "../hooks/useAddTexasGrouping";
import TexasGroupingDetails from "./TexasGroupingDetails";
import { ITexasGroupingDetails } from "../types";
import {
  addStateTexasGrouping,
  rowForEditTexasGrouping,
  editStateTexasGrouping,
  savingTexasGrouping,
} from "./../atoms/atoms";

interface TexasGroupingFormProps {
  isAdd: boolean;
  setIsAdd;
}

export const TexasGroupingForm: React.FC<TexasGroupingFormProps> = ({
  isAdd,
  setIsAdd,
}) => {
  const [rowForEdit, setRowForEdit] = useRecoilState(rowForEditTexasGrouping);
  const [saveModal, setSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useRecoilState(savingTexasGrouping);
  const [isAddState, setAddState] = useRecoilState(addStateTexasGrouping);
  const setEditState = useSetRecoilState(editStateTexasGrouping);
  const queryClient = useQueryClient();

  const [newData, setNewData] = useState<ITexasGroupingDetails>();
  const useFormMethods = useForm<ITexasGroupingDetails>({
    defaultValues: { ...rowForEdit },
  });
  const {
    formState: { isDirty },
  } = useFormMethods;

  // Add Texas Grouping
  const {
    mutate: TexasGroupingRows,
    isSuccess: addSuccess,
    isError: addError,
    data: addResult,
    reset: resetAddData,
  } = useAddTexasGrouping();

  const onSubmit = (data: ITexasGroupingDetails) => {
    setNewData({
      ...data,
      isActive: data.isActive ? true : false,
    });

    if (isAdd) {
      setSaveModal(true);
    } else {
      setIsSaving(false);
      if (!isDirty) {
        toast.fire("Saving Failed", "No Changes have been made", "warning");
      }
    }
  };

  const handleSave = () => {
    setSaveModal(false);
    TexasGroupingRows(newData);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setRowForEdit(undefined);
    setAddState(false);
    setEditState(false);
    setIsSaving(false);
    setIsAdd(false);
  };

  // Add Result
  if (addSuccess && addResult) {
    if (addResult.status === 501 || addResult.data === "Duplicate") {
      toast.fire("Failed", "Texas Grouping Already Exists.", "error");
      setIsSaving(!isSaving);
      setSaveModal(false);
    } else if (addResult.status === 201 || addResult.status === 200) {
      toast.fire("Success", "Texas Grouping Added Successfully.", "success");
      queryClient.invalidateQueries();
      handleCancel();
      setAddState(false);
      setIsAdd(false);
    } else if (addResult.status === 500) {
      if (
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available" ||
        addResult.data.message ===
          "Service not available, closing transmission channel. The server response was: 4.3.2 Service not available"
      ) {
        toast.fire(
          "Warning",
          "Texas Grouping added successfully, but " + addResult.data.message,
          "warning"
        );
        handleCancel();
      } else {
        toast.fire(
          "Failed",
          "Failed to Add Texas Grouping! No Data Saved",
          "error"
        );
      }
      queryClient.invalidateQueries();
      setAddState(false);
      setIsAdd(false);
    } else {
      toast.fire(
        "Server Error",
        "Failed to Add Texas Grouping! No Data Saved",
        "error"
      );
      queryClient.invalidateQueries();
      setAddState(false);
    }
    resetAddData();
    setSaveModal(false);
    setIsSaving(false);
  }
  if (addError) {
    setIsSaving(false);
    setSaveModal(false);
    toast.fire(
      "Server Error",
      "Failed to Add Texas Grouping! No Data Saved",
      "error"
    );
    queryClient.invalidateQueries();
    setAddState(false);
  }

  return (
    <>
      <div className="p-4">
        <div className=" p-3 space-y-2 border-l-2 border-primary box-shadow ">
          <FormProvider {...useFormMethods}>
            <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
              <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg">
                <div className="font-bold pt-1 pb text-primary text-base">
                  Add New Texas Grouping
                </div>
                <div className="flex justify-between gap-2 mr-2 pb-2 pt-3">
                  <SubmitHookForm
                    type="submit"
                    disabled={isSaving}
                    buttonText={isSaving ? "Saving.." : "Save"}
                    className={undefined}
                    onClick={undefined}
                  />
                  <CancelButton
                    onClick={(event) => {
                      event.preventDefault();
                      handleCancel();
                    }}
                  />
                </div>
              </div>
              <div className="border-b-2 mr-3 border-secondary"></div>
            </form>
            <div>
              <TexasGroupingDetails />
            </div>
          </FormProvider>
        </div>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={"Add Texas Grouping"}
          qConfirmation={"Are you sure you want to Add this data?"}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            handleSave();
            setIsSaving(true);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};

export default TexasGroupingForm;
